import React from "react";
import { makeAPI } from "../services/api";

import gsign_icon from "/src/assets/img/icons/google_sign_ico.png";
import applesign_icon from "/src/assets/img/icons/apple_sign_ico.png";
import eyeslash from '/assets/img/icon/eye-slash-solid.avif';
import eye from '/assets/img/icon/eye-solid.avif';


// import usePasswordValidator from "../components/PasswordValidator";

const VIEW = {
  LOGIN: "login",
  SIGNUP_REQUEST_OTP: "signup_request_otp",
  SIGNUP_VERIFY_OTP: "signup_verify_otp",
  FORGOT_REQUEST_OTP: "forgot_request_otp",
  FORGOT_VERIFY_OTP: "forgot_verify_otp",
  FORGOT_RESET_PASSWORD: "forgot_reset_password",
};

const OTP_ENDPOINTS = {
  signupRequest: [
    // "/api/request_signup_otp",
    // "/api/signup_request_otp",
    // "/api/send_signup_otp",
    "/api/send_otp",
  ],
  signupVerify: [
    // "/api/verify_signup_otp",
    // "/api/signup_verify_otp",
    "/api/verify_otp",
  ],
  forgotRequest: [
    "/api/send_otp",
  ],
  forgotVerify: [
    "/api/verify_otp",
  ],
};

const FIREBASE_SOCIAL_APP_URL = "https://www.gstatic.com/firebasejs/11.0.2/firebase-app.js";
const FIREBASE_SOCIAL_AUTH_URL = "https://www.gstatic.com/firebasejs/11.0.2/firebase-auth.js";
const EMAIL_OTP_ENABLED = String(import.meta.env.VITE_ENABLE_EMAIL_OTP || "").toLowerCase() === "true";
const OTP_LENGTH = 4;

let firebaseSocialRuntime = null;

function parseSocialFirebaseConfig() {
  const rawConfig =
    import.meta.env.VITE_FIREBASE_CONFIG ||
    import.meta.env.REACT_APP_FIREBASE_CONFIG ||
    "";

  if (!rawConfig) return null;

  try {
    return typeof rawConfig === "string" ? JSON.parse(rawConfig) : rawConfig;
  } catch (error) {
    console.error("Invalid social sign-in config:", error);
    return null;
  }
}

function getReferralId() {
  const url = new URL(window.location.href);
  const invite = Number(url.searchParams.get("invite"));
  if (Number.isFinite(invite) && invite > 0) return invite;
  const storedInvite = Number(sessionStorage.getItem("invite_id"));
  return Number.isFinite(storedInvite) && storedInvite > 0 ? storedInvite : 0;
}

function getReferralPayload() {
  const referelId = getReferralId();
  return referelId ? { referelId } : "";
}

function isSuccessResponse(response) {
  if (!response || response.status === "E") return false;
  return Boolean(response.status === "S" || response.token || response.success);
}

function responseMessage(response, fallback = "Request failed") {
  return response?.message || response?.error || fallback;
}

function isApplePrivateRelayEmail(email) {
  const normalizedEmail = String(email || "").trim().toLowerCase();
  return normalizedEmail.endsWith("@privaterelay.appleid.com");
}

async function callEndpointCandidates(candidates, payload) {
  let lastError = null;

  for (const endpoint of candidates) {
    try {
      const result = await makeAPI(endpoint, "POST", false, payload);
      if (isSuccessResponse(result)) {
        return result;
      }
      lastError = new Error(responseMessage(result, `Failed at ${endpoint}`));
    } catch (error) {
      lastError = error;
    }
  }

  throw (
    lastError ||
    new Error("No OTP backend endpoint responded. Please configure OTP routes.")
  );
}

async function loadSocialFirebaseAuth() {
  if (firebaseSocialRuntime) return firebaseSocialRuntime;

  const config = parseSocialFirebaseConfig();
  if (!config) {
    throw new Error("Social sign-in is temporarily unavailable. Please try again later.");
  }

  const appModule = await import(/* @vite-ignore */ FIREBASE_SOCIAL_APP_URL);
  const authModule = await import(/* @vite-ignore */ FIREBASE_SOCIAL_AUTH_URL);

  const app = appModule.getApps().length
    ? appModule.getApp()
    : appModule.initializeApp(config);
  const auth = authModule.getAuth(app);

  firebaseSocialRuntime = { auth, authModule };
  return firebaseSocialRuntime;
}

export default function Login() {
  const createOtpDigits = React.useCallback(() => Array.from({ length: OTP_LENGTH }, () => ""), []);

  const [view, setView] = React.useState(VIEW.LOGIN);
  const [busy, setBusy] = React.useState(false);
  const [errorMsg, setErrorMsg] = React.useState("");
  const [successMsg, setSuccessMsg] = React.useState("");

  const [loginForm, setLoginForm] = React.useState({ email: "", password: "" });
  const [signupForm, setSignupForm] = React.useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    country: "India",
    gender: "M",
    otp: "",
  });
  const [forgotForm, setForgotForm] = React.useState({
    email: "",
    otp: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = React.useState(false); // for password
  const [Confrim_Password, setConfrim_Password] = React.useState(false); // for confirm password
  const [isSignupPasswordFocused, setIsSignupPasswordFocused] = React.useState(false);
  const [isForgotPasswordFocused, setIsForgotPasswordFocused] = React.useState(false);

  const [otpRequestId, setOtpRequestId] = React.useState("");
  const [otpVerifyToken, setOtpVerifyToken] = React.useState("");
  const [otpCooldown, setOtpCooldown] = React.useState(0);
  const [signupOtpDigits, setSignupOtpDigits] = React.useState(createOtpDigits);
  const [forgotOtpDigits, setForgotOtpDigits] = React.useState(createOtpDigits);
  const signupOtpRefs = React.useRef([]);
  const forgotOtpRefs = React.useRef([]);

  const getPasswordRules = (password) => ({
    lowercase: /[a-z]/.test(password),
    uppercase: /[A-Z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[!@#$%^&*]/.test(password),
    minLength: password.length >= 8,
  });

  const getPasswordError = (passwordRules) => {
    if (!passwordRules.lowercase) return "Password needs at least one lowercase letter.";
    if (!passwordRules.uppercase) return "Password needs at least one uppercase letter.";
    if (!passwordRules.number) return "Password needs at least one number.";
    if (!passwordRules.special) return "Password needs at least one special character (!@#$%^&*).";
    if (!passwordRules.minLength) return "Password must be at least 8 characters long.";
    return "";
  };

  const signupPasswordRules = getPasswordRules(signupForm.password);
  const forgotPasswordRules = getPasswordRules(forgotForm.newPassword);
  const isSignupPasswordValid = Object.values(signupPasswordRules).every(Boolean);
  const isForgotPasswordValid = Object.values(forgotPasswordRules).every(Boolean);
  const isSignupPasswordMatch =
    signupForm.confirmPassword.length > 0 &&
    signupForm.password === signupForm.confirmPassword;
  const isForgetPasswordMatched = forgotForm.confirmPassword.length > 0 && forgotForm.newPassword === forgotForm.confirmPassword;
  const getSignupPasswordError = () => {
    return getPasswordError(signupPasswordRules);
  };

  const updateSignupOtp = (digits) => {
    setSignupOtpDigits(digits);
    setSignupForm((prev) => ({ ...prev, otp: digits.join("") }));
  };

  const updateForgotOtp = (digits) => {
    setForgotOtpDigits(digits);
    setForgotForm((prev) => ({ ...prev, otp: digits.join("") }));
  };

  const applyOtpInput = (digits, index, rawValue) => {
    const normalized = String(rawValue || "").replace(/\D/g, "");
    const next = [...digits];

    if (!normalized) {
      next[index] = "";
      return { nextDigits: next, focusIndex: index };
    }

    const values = normalized.slice(0, OTP_LENGTH - index).split("");
    values.forEach((digit, offset) => {
      next[index + offset] = digit;
    });

    return {
      nextDigits: next,
      focusIndex: Math.min(index + values.length, OTP_LENGTH - 1),
    };
  };

  const handleOtpChange = (flow, index, rawValue) => {
    const isSignup = flow === "signup";
    const digits = isSignup ? signupOtpDigits : forgotOtpDigits;
    const refs = isSignup ? signupOtpRefs : forgotOtpRefs;
    const updater = isSignup ? updateSignupOtp : updateForgotOtp;
    const { nextDigits, focusIndex } = applyOtpInput(digits, index, rawValue);
    updater(nextDigits);
    refs.current[focusIndex]?.focus();
    refs.current[focusIndex]?.select();
  };

  const handleOtpPaste = (flow, index, event) => {
    event.preventDefault();
    const pasted = event.clipboardData?.getData("text") || "";
    handleOtpChange(flow, index, pasted);
  };

  const handleOtpKeyDown = (flow, index, event) => {
    const isSignup = flow === "signup";
    const digits = isSignup ? signupOtpDigits : forgotOtpDigits;
    const refs = isSignup ? signupOtpRefs : forgotOtpRefs;
    const updater = isSignup ? updateSignupOtp : updateForgotOtp;

    if (event.key === "ArrowLeft") {
      event.preventDefault();
      refs.current[Math.max(index - 1, 0)]?.focus();
      return;
    }

    if (event.key === "ArrowRight") {
      event.preventDefault();
      refs.current[Math.min(index + 1, OTP_LENGTH - 1)]?.focus();
      return;
    }

    if (event.key === "Backspace") {
      event.preventDefault();
      const next = [...digits];

      if (next[index]) {
        next[index] = "";
        updater(next);
        return;
      }

      const previousIndex = Math.max(index - 1, 0);
      next[previousIndex] = "";
      updater(next);
      refs.current[previousIndex]?.focus();
    }
  };

  const renderOtpInputs = (flow, digits, refs) => (
    <>
      <label style={{ color: '#e4e4e4' }}>&nbsp;&nbsp;Enter OTP:</label>
      <div className="uk-margin" style={{ display: "flex", justifyContent: "center", gap: 8 }}>

        {digits.map((digit, index) => (
          <input
            key={`${flow}-otp-${index}`}
            ref={(element) => {
              refs.current[index] = element;
            }}
            className="uk-input otp-input"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete={index === 0 ? "one-time-code" : "off"}
            maxLength={1}
            value={digit}
            placeholder="0"
            onChange={(event) => handleOtpChange(flow, index, event.target.value)}
            onKeyDown={(event) => handleOtpKeyDown(flow, index, event)}
            onPaste={(event) => handleOtpPaste(flow, index, event)}
            style={{ width: 50, height: 50, textAlign: "center", fontSize: 18, borderRadius: '10px', padding: '0px' }}
          />
        ))}
      </div>
    </>
  );

  React.useEffect(() => {
    if (otpCooldown <= 0) return;
    const timer = setInterval(() => {
      setOtpCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [otpCooldown]);

  React.useEffect(() => {
    const modal = document.getElementById("modal-login");
    if (!modal) return;

    const resetModal = (event) => {
      if (event?.target !== modal) return;
      if (modal.classList.contains("uk-open")) return;
      setView(VIEW.LOGIN);
      setErrorMsg("");
      setSuccessMsg("");
      setBusy(false);
      setOtpRequestId("");
      setOtpVerifyToken("");
      setOtpCooldown(0);
      setSignupOtpDigits(createOtpDigits());
      setForgotOtpDigits(createOtpDigits());
      setSignupForm((prev) => ({ ...prev, otp: "" }));
      setForgotForm((prev) => ({ ...prev, otp: "" }));
    };

    modal.addEventListener("hidden", resetModal);
    modal.addEventListener("hidden.uk.modal", resetModal);
    return () => {
      modal.removeEventListener("hidden", resetModal);
      modal.removeEventListener("hidden.uk.modal", resetModal);
    };
  }, []);


  const closeModal = () => {
    if (window.UIkit && typeof window.UIkit.modal === "function") {
      window.UIkit.modal("#modal-login", { container: false }).hide();
    }
  };

  const withBusy = async (task) => {
    setBusy(true);
    setErrorMsg("");
    setSuccessMsg("");
    try {
      await task();
    } catch (error) {
      setErrorMsg(error?.message || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const goToSignup = () => {
    setErrorMsg("");
    setSuccessMsg("");
    setSignupOtpDigits(createOtpDigits());
    setSignupForm((prev) => ({ ...prev, otp: "" }));
    setView(VIEW.SIGNUP_REQUEST_OTP);
  };

  const goToForgot = () => {
    setErrorMsg("");
    setSuccessMsg("");
    setForgotOtpDigits(createOtpDigits());
    setForgotForm((prev) => ({ ...prev, otp: "" }));
    setView(VIEW.FORGOT_REQUEST_OTP);
  };

  const backToLogin = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setErrorMsg("");
    setSuccessMsg("");
    setOtpRequestId("");
    setOtpVerifyToken("");
    setOtpCooldown(0);
    setSignupOtpDigits(createOtpDigits());
    setForgotOtpDigits(createOtpDigits());
    setSignupForm((prev) => ({ ...prev, otp: "" }));
    setForgotForm((prev) => ({ ...prev, otp: "", newPassword: "", confirmPassword: "" }));
    setView(VIEW.LOGIN);
  };

  const persistSession = async (token, email) => {
    localStorage.setItem("token", token);

    try {
      const userData = await makeAPI("/api/get_user_data", "POST", true, {});
      if (userData?.status === "S" && userData?.data) {
        localStorage.setItem("user", JSON.stringify(userData.data));
      } else {
        localStorage.setItem("user", JSON.stringify({ email }));
      }
    } catch {
      localStorage.setItem("user", JSON.stringify({ email }));
    }

    if (window.cgesSDK && typeof window.cgesSDK === "function") {
      const sdk = new window.cgesSDK();
      if (sdk && typeof sdk.setToken === "function") {
        sdk.setToken(token);
      }
    }

    window.dispatchEvent(new CustomEvent("auth:updated"));
    window.location.reload();
    closeModal();
  };

  const handleEmailLogin = async (event) => {
    event.preventDefault();

    await withBusy(async () => {
      if (!loginForm.email || !loginForm.password) {
        throw new Error("Email and password are required.");
      }

      const response = await makeAPI("/api/login", "POST", false, {
        email: loginForm.email.trim().toLowerCase(),
        password: loginForm.password,
      });

      if (response?.status !== "S" || !response?.token) {
        throw new Error(responseMessage(response, "Unable to login."));
      }

      await persistSession(response.token, loginForm.email.trim().toLowerCase());
      setSuccessMsg("Login successful.");
    });
  };

  const handleSocialSignIn = async (providerName) => {
    await withBusy(async () => {
      const { auth, authModule } = await loadSocialFirebaseAuth();
      const referelId = getReferralPayload();
      let provider;
      let endpoint;

      if (providerName === "google") {
        provider = new authModule.GoogleAuthProvider();
        endpoint = "/api/google_login";
      } else {
        provider = new authModule.OAuthProvider("apple.com");
        provider.addScope("email");
        provider.addScope("name");
        endpoint = "/api/apple_login";
      }

      const credential = await authModule.signInWithPopup(auth, provider);
      const socialEmail = credential?.user?.email?.trim().toLowerCase() || "";

      if (providerName === "apple" && isApplePrivateRelayEmail(socialEmail)) {
        await authModule.signOut(auth);
        throw new Error("Apple Sign In with Hide My Email is not allowed. Please use your real email address to create or login to your account.");
      }

      const idToken = await credential.user.getIdToken();

      const response = await makeAPI(endpoint, "POST", false, { idToken, referelId });
      if (!response?.token) {
        throw new Error(responseMessage(response, `${providerName} login failed.`));
      }

      await persistSession(response.token, socialEmail);
      setSuccessMsg("Login successful.");
    });
  };

  const requestSignupOtp = async (event) => {
    event.preventDefault();

    await withBusy(async () => {
      if (!EMAIL_OTP_ENABLED) {
        await completeSignup();
        return;
      }

      const email = signupForm.email.trim().toLowerCase();
      if (!signupForm.name.trim()) throw new Error("Name is required.");
      if (!email) throw new Error("Email is required.");
      if (!signupForm.password) throw new Error("Password is required.");
      if (!isSignupPasswordValid) throw new Error(getSignupPasswordError());
      if (signupForm.password !== signupForm.confirmPassword) {
        throw new Error("Passwords do not match.");
      }
      const password = signupForm.password;
      const name = signupForm.name.trim();
      const referelId = getReferralPayload();
      const response = await callEndpointCandidates(OTP_ENDPOINTS.signupRequest, {
        email,
        password,
        name,
        country: signupForm.country || "",
        gender: signupForm.gender || "",
        userCredential: null,
        referelId,
        flow: "signup",
      });

      const requestId = response?.request_id || response?.data?.request_id || "";
      setOtpRequestId(requestId);
      setOtpCooldown(30);
      setSignupOtpDigits(createOtpDigits());
      setSignupForm((prev) => ({ ...prev, otp: "" }));
      setView(VIEW.SIGNUP_VERIFY_OTP);
      setSuccessMsg(responseMessage(response, "OTP sent to your email."));
    });
  };

  const completeSignup = async () => {
    const email = signupForm.email.trim().toLowerCase();
    const password = signupForm.password;
    const name = signupForm.name.trim();
    // const response = await makeAPI("/api/signup", "POST", false, {
    //   email,
    //   password,
    //   name,
    //   country: signupForm.country || "",
    //   gender: signupForm.gender || "",
    //   userCredential: null,
    //   ...getReferralPayload(),
    // });

    // if (response?.status !== "S") {
    //   throw new Error(responseMessage(response, "Signup failed."));
    // }

    setSuccessMsg("Account created successfully. Please login to continue!.");
    setView(VIEW.LOGIN);
    setLoginForm({ email, password: "" });
  };

  const verifySignupOtp = async (event) => {
    event.preventDefault();

    await withBusy(async () => {
      if (!signupForm.otp.trim()) throw new Error("OTP is required.");

      const response = await callEndpointCandidates(OTP_ENDPOINTS.signupVerify, {
        email: signupForm.email.trim().toLowerCase(),
        otp: signupForm.otp.trim(),
        request_id: otpRequestId,
        flow: "signup",
      });

      if (!isSuccessResponse(response)) {
        throw new Error(responseMessage(response, "Invalid OTP."));
      }

      if (response?.token) {
        setOtpVerifyToken(response.token);
      }
      await completeSignup();
    });
  };

  const requestForgotOtp = async (event) => {
    event.preventDefault();

    await withBusy(async () => {
      if (!EMAIL_OTP_ENABLED) {
        setView(VIEW.FORGOT_RESET_PASSWORD);
        setSuccessMsg("OTP flow is disabled. Set a new password directly.");
        return;
      }

      const email = forgotForm.email.trim().toLowerCase();
      if (!email) throw new Error("Email is required.");

      // const validate = await makeAPI("/api/validate_user", "POST", false, { email });
      // if (validate?.status !== "S") {
      //   throw new Error("Account does not exist for this email.");
      // }

      const response = await callEndpointCandidates(OTP_ENDPOINTS.forgotRequest, {
        email,
        flow: "forgot_password",
      });

      const requestId = response?.request_id || response?.data?.request_id || "";
      setOtpRequestId(requestId);
      setOtpCooldown(30);
      setForgotOtpDigits(createOtpDigits());
      setForgotForm((prev) => ({ ...prev, otp: "" }));
      setView(VIEW.FORGOT_VERIFY_OTP);
      setSuccessMsg(responseMessage(response, "OTP sent to your email."));
    });
  };

  const verifyForgotOtp = async (event) => {
    event.preventDefault();

    await withBusy(async () => {
      if (!forgotForm.otp.trim()) throw new Error("OTP is required.");

      const response = await callEndpointCandidates(OTP_ENDPOINTS.forgotVerify, {
        email: forgotForm.email.trim().toLowerCase(),
        otp: forgotForm.otp.trim(),
        request_id: otpRequestId,
        flow: "forgot_password",
      });

      if (!isSuccessResponse(response)) {
        throw new Error(responseMessage(response, "Invalid OTP."));
      }

      if (response?.token) {
        setOtpVerifyToken(response.token);
      }
      setView(VIEW.FORGOT_RESET_PASSWORD);
      setSuccessMsg("OTP verified. Set your new password.");
    });
  };

  const resetForgotPassword = async (event) => {
    event.preventDefault();

    await withBusy(async () => {
      if (!forgotForm.newPassword) throw new Error("New password is required.");
      if (!isForgotPasswordValid) throw new Error(getPasswordError(forgotPasswordRules));
      if (forgotForm.newPassword !== forgotForm.confirmPassword) {
        throw new Error("Passwords do not match.");
      }
      if (!otpVerifyToken) {
        throw new Error("Session expired. Please verify OTP again.");
      }

      const response = await makeAPI("/api/update_password", "POST", otpVerifyToken, {
        email: forgotForm.email.trim().toLowerCase(),
        password: forgotForm.newPassword,
      });

      if (response?.status !== "S") {
        throw new Error(responseMessage(response, "Unable to update password."));
      }

      setSuccessMsg("Password updated successfully. Please login.");
      setView(VIEW.LOGIN);
      setLoginForm({ email: forgotForm.email.trim().toLowerCase(), password: "" });
      setForgotForm({ email: "", otp: "", newPassword: "", confirmPassword: "" });
      setForgotOtpDigits(createOtpDigits());
      setOtpRequestId("");
      setOtpVerifyToken("");
    });
  };

  return (
    <>
      <style>{`
    body.dark-theme .form-login .uk-input{
      color:#e4e4e4;
      background:#000;
      border: 1px solid #3d3d3d
    }

    body.dark-theme .form-login .uk-input::placeholder{
      color: #e4e4e4
    }
    .form-login .otp-input::placeholder,
    body.dark-theme .form-login .otp-input::placeholder {
      color: #585858 !important;
    }

    .uk-alert-danger
    {
      background:#000;
      font-size:14px;
      padding: 10px;
      color: red;
      border-radius:10px 10px;
      text-align:center;
    }

    .uk-alert-success
    {
      background:#000;
      padding: 10px;
      color: #00fa0c;
      border-radius:10px 10px;
      text-align:center;
    }

    `}</style>
      <div className="uk-flex-top" id="modal-login" data-uk-modal="container: false">
        <div className="uk-modal-dialog uk-modal-body uk-margin-auto-vertical">
          <button className="uk-modal-close-default text-white" type="button" data-uk-close></button>

          <div className="form-login" style={{ margin: 0, maxWidth: "100%" }}>
            <div className="form-login__box">
              <div className="form-login__social" style={{ marginBottom: 20, display: 'block' }}>
                <button
                  className="uk-button uk-button-default"
                  type="button"
                  onClick={() => handleSocialSignIn("google")}
                  disabled={busy}
                  style={{ marginRight: 8, color: '#e4e4e4' }}
                >
                  <img src={gsign_icon} width={20} alt="Google SignIn" style={{ marginTop: '-3px' }} />

                  &nbsp;&nbsp;Continue with Google
                </button><br /><br />
                <button
                  className="uk-button uk-button-default"
                  type="button"
                  onClick={() => handleSocialSignIn("apple")}
                  disabled={busy}
                  style={{ marginRight: 8, color: '#e4e4e4' }}
                >
                  <img src={applesign_icon} width={20} alt="Apple SignIn" style={{ marginTop: '-3px' }} />
                  &nbsp;&nbsp;Continue with Apple
                </button>
              </div>

              <div className="uk-heading-line uk-text-center">
                <span style={{ marginRight: 8, color: '#ff810e' }}>Email Authentication</span>
              </div>

              {errorMsg ? (
                <div className="uk-alert-danger uk-margin-small" data-uk-alert>
                  {errorMsg}
                </div>
              ) : null}

              {successMsg ? (
                <div className="uk-alert-success uk-margin-small" data-uk-alert>
                  {successMsg}
                </div>
              ) : null}

              {view === VIEW.LOGIN ? (
                <form onSubmit={handleEmailLogin}>
                  <div className="uk-margin">
                    <input
                      className="uk-input"
                      type="email"
                      placeholder="Email"
                      value={loginForm.email}
                      onChange={(e) => setLoginForm((prev) => ({ ...prev, email: e.target.value }))}
                      autoComplete="email"
                    />
                  </div>
                  <div className="uk-margin position-relative">
                    <input
                      className="uk-input"
                      type={showPassword ? "text":"password"}
                      placeholder="Password"
                      value={loginForm.password}
                      onChange={(e) =>
                        setLoginForm((prev) => ({ ...prev, password: e.target.value }))
                      }

                      autoComplete="current-password"
                    />
                    <span
                      onClick={() => setShowPassword(!showPassword)}
                      style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer' }}
                    >
                      <img src={showPassword ? "/assets/img/icon/eye-solid.avif" : "/assets/img/icon/eye-slash-solid.avif"} alt="img" width="18" />
                    </span>
                  </div>
                  <div className="uk-margin">
                    <button className="uk-button uk-button-danger uk-width-1-1" style={{ color: '#fff', fontSize: '16px', fontWeight: 'bolder' }} type="submit" disabled={busy}>
                      {busy ? "Please wait..." : "Log In"}
                    </button>
                  </div>
                  <div className="uk-margin uk-text-center">
                    <a
                      href="#!"
                      onClick={(e) => {
                        e.preventDefault();
                        goToForgot();
                      }}
                    >
                      Forgotten password?
                    </a>
                  </div>
                  <hr />
                  <div className="uk-text-center" style={{ color: '#e4e4e4' }}>
                    <span>Don&apos;t have an account?</span>
                    <button
                      className="uk-button uk-button-link uk-margin-small-left"
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (busy) return;
                        goToSignup();
                      }}
                      disabled={busy}
                      style={{ padding: 0, minHeight: "auto", lineHeight: "inherit", textDecoration: 'underline' }}
                    >
                      Register
                    </button>
                  </div>
                </form>
              ) : null}

              {view === VIEW.SIGNUP_REQUEST_OTP ? (
                <form onSubmit={requestSignupOtp}>
                  <div className="uk-margin">
                    <input
                      className="uk-input"
                      type="text"
                      placeholder="Full Name"
                      value={signupForm.name}
                      maxLength={40}
                      minLength={4}
                      onChange={(e) => setSignupForm((prev) => ({ ...prev, name: e.target.value }))}
                    />
                  </div>
                  <div className="uk-margin">
                    <input
                      className="uk-input"
                      type="email"
                      placeholder="Email"
                      value={signupForm.email}
                      onChange={(e) => setSignupForm((prev) => ({ ...prev, email: e.target.value }))}
                    />
                  </div>

                  <div className="uk-margin position-relative">
                    <input
                      className="uk-input"
                      type={showPassword ? "text" : "password"}
                      placeholder="Password"
                      id="sign-up-password"
                      value={signupForm.password}
                      maxLength={30}
                      minLength={5}
                      onChange={(e) => setSignupForm((prev) => ({ ...prev, password: e.target.value }))}
                      onFocus={() => setIsSignupPasswordFocused(true)}
                      onBlur={() => setIsSignupPasswordFocused(false)}
                      style={{ paddingRight: '40px' }}
                    />
                    <span
                      onClick={() => setShowPassword(!showPassword)}
                      style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer' }}
                    >
                      <img src={showPassword ? "/assets/img/icon/eye-solid.avif" : "/assets/img/icon/eye-slash-solid.avif"} alt="img" width="18" />
                    </span>
                  </div>
                  <div
                    className="password_req"
                    style={{
                      color: "#e4e4e4",
                      fontSize: 13,
                      overflow: "hidden",
                      maxHeight: isSignupPasswordFocused ? 200 : 0,
                      opacity: isSignupPasswordFocused ? 1 : 0,
                      transform: isSignupPasswordFocused ? "translateY(0)" : "translateY(-4px)",
                      transition: "max-height 2s ease, opacity 2s ease, transform 200ms ease",
                      pointerEvents: isSignupPasswordFocused ? "auto" : "none",
                    }}
                  >
                    <p>
                      Password must contain: <span style={{color:"var(--main-color-one)"}}>{isSignupPasswordValid ? "OK" : "Missing requirements"}</span>
                    </p>
                    <ul className="uk-list uk-list-bullet uk-margin-remove-top uk-margin-small-bottom">
                      <li
                        className={`lowercase ${signupPasswordRules.lowercase ? "active" : ""}`}
                        style={{ color: signupPasswordRules.lowercase ? "#32d296" : "#f0506e", transition: "color 200ms ease" }}
                      >
                        One lowercase letter
                      </li>
                      <li
                        className={`uppercase ${signupPasswordRules.uppercase ? "active" : ""}`}
                        style={{ color: signupPasswordRules.uppercase ? "#32d296" : "#f0506e", transition: "color 200ms ease" }}
                      >
                        One uppercase letter
                      </li>
                      <li
                        className={`number ${signupPasswordRules.number ? "active" : ""}`}
                        style={{ color: signupPasswordRules.number ? "#32d296" : "#f0506e", transition: "color 200ms ease" }}
                      >
                        One number
                      </li>
                      <li
                        className={`special ${signupPasswordRules.special ? "active" : ""}`}
                        style={{ color: signupPasswordRules.special ? "#32d296" : "#f0506e", transition: "color 200ms ease" }}
                      >
                        One special character (!@#$%^&*)
                      </li>
                      <li
                        className={`characters ${signupPasswordRules.minLength ? "active" : ""}`}
                        style={{ color: signupPasswordRules.minLength ? "#32d296" : "#f0506e", transition: "color 200ms ease" }}
                      >
                        Minimum 8 characters
                      </li>
                    </ul>
                  </div>
                  <div className="uk-margin position-relative">
                    <input
                      className="uk-input"
                      type={Confrim_Password ? "text" : "password"}
                      id="sign-up-confirm-password"
                      placeholder="Confirm Password"
                      maxLength={30}
                      minLength={5}
                      value={signupForm.confirmPassword}
                      onChange={(e) =>
                        setSignupForm((prev) => ({ ...prev, confirmPassword: e.target.value }))
                      }
                      onFocus={() => setIsSignupPasswordFocused(false)}
                      style={{ paddingRight: '40px' }}
                      disabled={!isSignupPasswordValid}
                    />
                    <span
                      onClick={() => setConfrim_Password(!Confrim_Password)}
                      style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer' }}
                    >
                      <img src={Confrim_Password ? "/assets/img/icon/eye-solid.avif" : "/assets/img/icon/eye-slash-solid.avif"} alt="img" width="18" />
                    </span>
                  </div>
                  {signupForm.confirmPassword ? (
                    <div
                      id="retype-error"
                      className="uk-margin-small-top"
                      style={{ color: isSignupPasswordMatch ? "#32d296" : "#f0506e", fontSize: 13 }}
                    >
                      {isSignupPasswordMatch ? "Password Match..!" : "Passwords don't match!"}
                    </div>
                  ) : null}
                  <div className="uk-margin">
                    <button
                      className="uk-button uk-button-danger uk-width-1-1"
                      style={{ color: '#fff', fontSize: '16px', fontWeight: 'bolder' }}
                      type="submit"
                      disabled={busy || !isSignupPasswordValid || !isSignupPasswordMatch}
                    >
                      {busy ? "Sending OTP..." : "Send OTP"}
                    </button>
                  </div>
                  <div className="uk-text-center">
                    <a
                      href="#!"
                      onClick={backToLogin}
                    >
                      Back to login
                    </a>
                  </div>
                </form>
              ) : null}

              {view === VIEW.SIGNUP_VERIFY_OTP ? (
                <form onSubmit={verifySignupOtp}>
                  {renderOtpInputs("signup", signupOtpDigits, signupOtpRefs)}
                  <div className="uk-margin">
                    <button className="uk-button uk-button-danger uk-width-1-1" style={{ color: '#fff', fontSize: '16px', fontWeight: 'bolder' }} type="submit" disabled={busy}>
                      {busy ? "Verifying..." : "Verify OTP & Create Account"}
                    </button>
                  </div>
                  <div className="uk-text-center">
                    <a
                      href="#!"
                      onClick={(e) => {
                        e.preventDefault();
                        if (otpCooldown > 0 || busy) return;
                        requestSignupOtp({ preventDefault: () => { } });
                      }}
                    >
                      {otpCooldown > 0 ? `Resend OTP in ${otpCooldown}s` : "Resend OTP"}
                    </a>
                  </div>
                  <div className="uk-text-center uk-margin-small-top">
                    <a href="#!" onClick={backToLogin}>
                      Back to login
                    </a>
                  </div>
                </form>
              ) : null}

              {view === VIEW.FORGOT_REQUEST_OTP ? (
                <form onSubmit={requestForgotOtp}>
                  <div className="uk-margin">
                    <input
                      className="uk-input"
                      type="email"
                      placeholder="Registered Email"
                      value={forgotForm.email}
                      onChange={(e) => setForgotForm((prev) => ({ ...prev, email: e.target.value }))}
                    />
                  </div>
                  <div className="uk-margin">
                    <button className="uk-button uk-button-danger uk-width-1-1" type="submit" style={{ color: '#fff', fontSize: '16px', fontWeight: 'bolder' }} disabled={busy}>
                      {busy ? "Sending OTP..." : "Send OTP"}
                    </button>
                  </div>
                  <div className="uk-text-center">
                    <a
                      href="#!"
                      onClick={backToLogin}
                    >
                      Back to login
                    </a>
                  </div>
                </form>
              ) : null}

              {view === VIEW.FORGOT_VERIFY_OTP ? (
                <form onSubmit={verifyForgotOtp}>
                  {renderOtpInputs("forgot", forgotOtpDigits, forgotOtpRefs)}
                  <div className="uk-margin">
                    <button className="uk-button uk-button-danger uk-width-1-1" style={{ color: '#fff', fontSize: '16px', fontWeight: 'bolder' }} type="submit" disabled={busy}>
                      {busy ? "Verifying..." : "Verify OTP"}
                    </button>
                  </div>
                  <div className="uk-text-center">
                    <a
                      href="#!"
                      onClick={(e) => {
                        e.preventDefault();
                        if (otpCooldown > 0 || busy) return;
                        requestForgotOtp({ preventDefault: () => { } });
                      }}
                    >
                      {otpCooldown > 0 ? `Resend OTP in ${otpCooldown}s` : "Resend OTP"}
                    </a>
                  </div>
                  <div className="uk-text-center uk-margin-small-top">
                    <a href="#!" onClick={backToLogin}>
                      Back to login
                    </a>
                  </div>
                </form>
              ) : null}

              {view === VIEW.FORGOT_RESET_PASSWORD ? (
                <form onSubmit={resetForgotPassword}>
                  <div className="uk-margin position-relative">
                    <input
                      className="uk-input"
                      type={showPassword ? "text":"password"}
                      placeholder="New Password"
                      maxLength={30}
                      minLength={5}
                      value={forgotForm.newPassword}
                      onChange={(e) =>
                        setForgotForm((prev) => ({ ...prev, newPassword: e.target.value }))
                      }
                      onFocus={() => setIsForgotPasswordFocused(true)}
                      onBlur={() => setIsForgotPasswordFocused(false)}
                    />
                    <span
                      onClick={() => setShowPassword(!showPassword)}
                      style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer' }}
                    >
                      <img src={showPassword ? "/assets/img/icon/eye-solid.avif" : "/assets/img/icon/eye-slash-solid.avif"} alt="img" width="18" />
                    </span>
                  </div>
                  <div
                    className="password_req"
                    style={{
                      color: "#e4e4e4",
                      fontSize: 13,
                      overflow: "hidden",
                      maxHeight: isForgotPasswordFocused ? 200 : 0,
                      opacity: isForgotPasswordFocused ? 1 : 0,
                      transform: isForgotPasswordFocused ? "translateY(0)" : "translateY(-4px)",
                      transition: "max-height 2s ease, opacity 2s ease, transform 200ms ease",
                      pointerEvents: isForgotPasswordFocused ? "auto" : "none",
                    }}
                  >
                    <p>
                      Password must contain: <span style={{ color: "var(--main-color-one)" }}>{isForgotPasswordValid ? "OK" : "Missing requirements"}</span>
                    </p>
                    <ul className="uk-list uk-list-bullet uk-margin-remove-top uk-margin-small-bottom">
                      <li
                        className={`lowercase ${forgotPasswordRules.lowercase ? "active" : ""}`}
                        style={{ color: forgotPasswordRules.lowercase ? "#32d296" : "#f0506e", transition: "color 200ms ease" }}
                      >
                        One lowercase letter
                      </li>
                      <li
                        className={`uppercase ${forgotPasswordRules.uppercase ? "active" : ""}`}
                        style={{ color: forgotPasswordRules.uppercase ? "#32d296" : "#f0506e", transition: "color 200ms ease" }}
                      >
                        One uppercase letter
                      </li>
                      <li
                        className={`number ${forgotPasswordRules.number ? "active" : ""}`}
                        style={{ color: forgotPasswordRules.number ? "#32d296" : "#f0506e", transition: "color 200ms ease" }}
                      >
                        One number
                      </li>
                      <li
                        className={`special ${forgotPasswordRules.special ? "active" : ""}`}
                        style={{ color: forgotPasswordRules.special ? "#32d296" : "#f0506e", transition: "color 200ms ease" }}
                      >
                        One special character (!@#$%^&*)
                      </li>
                      <li
                        className={`characters ${forgotPasswordRules.minLength ? "active" : ""}`}
                        style={{ color: forgotPasswordRules.minLength ? "#32d296" : "#f0506e", transition: "color 200ms ease" }}
                      >
                        Minimum 8 characters
                      </li>
                    </ul>
                  </div>
                  <div className="uk-margin position-relative">
                    <input
                      className="uk-input"
                      type={Confrim_Password ? "text":"password"}
                      placeholder="Confirm New Password"
                      maxLength={30}
                      minLength={5}
                      value={forgotForm.confirmPassword}
                      onChange={(e) =>
                        setForgotForm((prev) => ({ ...prev, confirmPassword: e.target.value }))
                      }
                    />
                    <span
                      onClick={() => setConfrim_Password(!Confrim_Password)}
                      style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer' }}
                    >
                      <img src={Confrim_Password ? "/assets/img/icon/eye-solid.avif" : "/assets/img/icon/eye-slash-solid.avif"} alt="img" width="18" />
                    </span>
                  </div>
                  {forgotForm.confirmPassword ? (
                    <div
                      id="retype-error"
                      className="uk-margin-small-top"
                      style={{ color: isForgetPasswordMatched ? "#32d296" : "#f0506e", fontSize: 13 }}
                    >
                      {isForgetPasswordMatched ? "Password Match..!" : "Passwords don't match!"}
                    </div>
                  ) : null}
                  <div className="uk-margin">
                    <button className="uk-button uk-button-danger uk-width-1-1" style={{ color: '#fff', fontSize: '16px', fontWeight: 'bolder' }} type="submit" disabled={busy || !isForgotPasswordValid}>
                      {busy ? "Updating..." : "Update Password"}
                    </button>
                  </div>
                  <div className="uk-text-center">
                    <a href="#!" onClick={backToLogin}>
                      Back to login
                    </a>
                  </div>
                </form>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
